import React, { useState } from 'react';

import { dayjs } from '../../../utils/dayjs-utils';
import { downloadFiles, formatFollowers, formatNumber, getPlatformImage } from '../../../utils/global';
import CommentSentimentTag from '../../Dashboard/SocialListening/components/CampaignDetails/CommentSentimentTag';
import LabellingModal from '../../Dashboard/SocialListening/components/CampaignDetails/Labelling/LabellingModal';
import ContentCard from '../../Dashboard/SocialListening/components/ContentCard';
import RequestUsageRightsModal from '../../Dashboard/SocialListening/components/modals/RequestUsageRightsModal';
import ViewUsageRightsModal from '../../Dashboard/SocialListening/components/modals/ViewUsageRightsModal';
import UsageRequestButton from '../../Dashboard/SocialListening/components/UsageRequestButton';
import AvatarComponent from '../AvatarComponent/AvatarComponent';
import ModalComponent from '../ModalComponent/ModalComponent';

import './ContentPreviewModal.scss';

import { useCampaignDetail } from '@/components/Dashboard/SocialListening/components/CampaignDetails/CampaignDetailProvider';
import LabelGroup from '@/components/Dashboard/SocialListening/components/CampaignDetails/ContentListSection/LabelGroup';
import PersonIcon from '@/components/Icons/icons/PersonIcon';
import BookmarkIcon from '@/components/Icons/icons/PlayerIcon/BookmarkIcon';
import DownloadIcon from '@/components/Icons/icons/PlayerIcon/DownloadIcon';

import CustomTooltip from '../CustomTooltip/CustomTooltip';
import FtuxComponent from '../FtuxComponent/FtuxComponent';

interface ContentPreviewModalProps {
  show: boolean;
  onClose: () => void;
  post: any;
}

const ContentPreviewModal: React.FC<ContentPreviewModalProps> = ({ show, onClose, post }) => {
  const { labelModalOpen, setLabelModalOpen } = useCampaignDetail();

  const [showViewUsageRightsModal, setShowViewUsageRightsModal] = useState(false);
  const [showRequestUsageRightsModal, setShowRequestUsageRightsModal] = useState(false);

  function openRequestUsageRequestModal() {
    setShowRequestUsageRightsModal(true);
  }

  function openViewUsageRequestModal() {
    setShowViewUsageRightsModal(true);
  }

  const onDownloadClick = () => {
    for (const media of post?.media) {
      downloadFiles(media?.url);
    }
  };

  const navigateToIIV = () => {
    window.open(`/dashboard/profile/${post?.profile?.listId}/${post?.profile?._id}`, '_blank');
  };

  // Type assertions for the modal components
  const RequestModal = RequestUsageRightsModal as React.FC<{
    onClose: () => void;
    post: any;
    refreshLogPosts: () => void;
  }>;

  const ViewModal = ViewUsageRightsModal as React.FC<{
    onClose: () => void;
    post: any;
  }>;

  const LabelModal = LabellingModal as React.FC<{
    labelModalOpen: any;
    setOpenModal: any;
  }>;

  return (
    <>
      <ModalComponent show={show} onClose={onClose} width="700px" modalBackdrop="dark">
        <div className="content-preview-modal">
          <div className="content-preview-modal__left">
            <ContentCard post={post} hideCardBottom hideCardTop />
          </div>
          <div className="content-preview-modal__right">
            {/* Profile Section */}
            <div className="profile-section">
              <div className="profile-section__info">
                <AvatarComponent src={post?.profile?.imageUrl} alt={post.profile?.imageUrl} size={32} />
                <div className="profile-section__details">
                  <div className="username">
                    <img src={getPlatformImage(post?.entityUrl)} alt={post?.platform} className="platform-logo" />@
                    {post.profile?.username}
                  </div>
                  <div className="followers">{formatFollowers(post.profile?.followers)} followers</div>
                </div>
              </div>
              <div className="post-actions">
                <CustomTooltip title="View Profile">
                  <PersonIcon color="var(--icon-subdued)" onClick={navigateToIIV} />
                </CustomTooltip>
                <CustomTooltip title="Download">
                  <DownloadIcon color="var(--icon-subdued)" width={18} height={18} onClick={onDownloadClick} />
                </CustomTooltip>
              </div>
            </div>

            <div className="content-divider" />

            {/* Caption Section */}
            <div className="caption-section">
              {post?.type !== 'story' && (
                <p>
                  <span className="username">@{post?.profile?.username}</span> {post?.caption}
                </p>
              )}
              {post?.type === 'story' && (
                <FtuxComponent
                  icon="💬"
                  width={184}
                  iconBgColor="var(--grey-bg)"
                  title="Caption & Comments are unavailable for stories"
                  titleStyle={{ fontSize: '14px', fontWeight: '400', color: 'var(--text-subdued)' }}
                  textStyle={{ maxWidth: '280px', color: 'var(--text-default)' }}
                />
              )}
            </div>
            <LabelGroup labels={post?.labels} showAllLabels={true} />

            <div className="content-divider" />

            {/* Actions Section */}
            <div className="actions-section">
              <UsageRequestButton
                buttonStyle={{ fontSize: '14px' }}
                status={post?.usageRightStatus}
                openView={openViewUsageRequestModal}
                openCreate={openRequestUsageRequestModal}
              />

              <div
                className="action-item"
                onClick={() => {
                  setLabelModalOpen({ post: post, openModal: 'addLabel' });
                }}>
                <BookmarkIcon color="var(--icon-subdued)" />
                <span>Label</span>
              </div>
            </div>
            <div className="content-divider" />

            {/* Stats Section */}
            <div className="stats-section">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="label">Date Posted</div>
                  <div className="value">{dayjs(post?.creativeCreatedDate).format('DD MMM YYYY')}</div>
                </div>
                <div className="stat-item">
                  <div className="label">Engagement Rate</div>
                  <div className="value">{post?.engagementRate ? `${post.engagementRate.toFixed(1)}%` : '-'}</div>
                </div>
                <div className="stat-item">
                  <div className="label">Media Value</div>
                  <div className="value">{post?.contentValue ? `$${formatNumber(post?.contentValue)}` : '-'}</div>
                </div>
                {post?.type !== 'story' && (
                  <div className="stat-item">
                    <div className="label">Comment Sentiment</div>
                    <CommentSentimentTag
                      sentiment={post?.sentiment?.toLowerCase()}
                      comments={post?.comments}
                      type={post.type}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalComponent>

      {showViewUsageRightsModal && <ViewModal onClose={() => setShowViewUsageRightsModal(false)} post={post} />}
      {showRequestUsageRightsModal && (
        <RequestModal onClose={() => setShowRequestUsageRightsModal(false)} post={post} refreshLogPosts={() => {}} />
      )}
      {labelModalOpen?.post && <LabelModal labelModalOpen={labelModalOpen} setOpenModal={setLabelModalOpen} />}
    </>
  );
};

export default ContentPreviewModal;
